import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip
} from '@material-ui/core';
import Avatar from '@material-ui/core/Avatar';
import FolderIcon from '@material-ui/icons/InsertDriveFile';
import IconButton from '@material-ui/core/IconButton';
import InventoryIcon from '@material-ui/icons/ListAlt';
import ChangelogIcon from '@material-ui/icons/History';
import PropTypes from 'prop-types';
import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import grey from '@material-ui/core/colors/grey';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import ConfirmationDialogOnClickWrapper from '../../../../components/ConfirmationDialogOnClickWrapper';
import Can from '../../../../components/Can';
import ShelfRowColumnInput from './ShelfRowColumnInput';

const useStyles = makeStyles(theme => ({
  lowStockIconColor: {
    color: theme.palette.error.main
  },
  noInventoryColor: {
    color: grey[400]
  },
  withStockIconColor: {
    color: theme.palette.success.main
  },
  vtkIconColor: {
    backgroundColor: 'blueviolet'
  },
  table: {
    overflow: 'hidden',
    '& .MuiTableCell-root': {
      borderBottom: 'none'
    }
  },
  tableRow: {
    '& td, th': {
      fontSize: '1rem'
    },
    '& th': {
      paddingTop: '10px',
      paddingBottom: '5px'
    },
    '& td:not(:first-child)': {
      padding: '8px'
    },
    '& td:first-child': {
      paddingLeft: '16px',
      paddingRight: '8px',
      paddingTop: '8px',
      paddingBottom: '8px'
    }
  },
  shelfHeader: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr 50px',
    alignItems: 'center',
    justifyContent: 'center',
    width: '250px',
    textAlign: 'center'
  },
  shelfHeaderCell: {
    paddingRight: '8px !important',
    paddingLeft: '8px !important'
  },
  shelfInputCell: {
    paddingRight: '8px !important',
    paddingLeft: '8px !important'
  },
  hiddenHeaderCell: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '50px'
  },
  hiddenInputCell: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '50px'
  },
  hiddenBracket: {
    opacity: 0.3
  }
}));

const FileList = ({
  data,
  onDelete,
  onDownload,
  onOpenInventory,
  showChangelog,
  removingItems = [],
  onEdit,
  onReplace,
  organizationId,
  onUpdate
}) => {
  const ITEM_HEIGHT = 48;
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [currentBracket, setCurrentBracket] = React.useState(null);
  const open = Boolean(anchorEl);

  const moreOptionHandler = bracket => event => {
    setAnchorEl(event.currentTarget);
    setCurrentBracket(bracket);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setCurrentBracket(null);
  };

  if (data === null) {
    return '';
  }

  const inventoryIconClassName = bracket => {
    if (!bracket.has_inventory) {
      return classes.noInventoryColor;
    }

    if (bracket.low_stock) {
      return classes.lowStockIconColor;
    }
    return classes.withStockIconColor;
  };

  if (data.length === 0) {
    return null;
  }

  const createOnMenuClickCallback = callback => () => {
    callback();
    handleClose();
  };

  const createToggleHideHandler = bracket => () =>
    onUpdate(bracket.id, { ...bracket, hide: !bracket.hidden_at });

  const removing = currentBracket ? removingItems.indexOf(currentBracket.id) !== -1 : false;
  let deletionMessage =
    currentBracket && currentBracket.users.length > 0
      ? `The library is assigned to ${currentBracket.users.length} member${
          currentBracket.users.length > 1 ? 's' : ''
        }.`
      : '';
  deletionMessage += ' Are you sure you want to delete this file?';

  return (
    <>
      <TableContainer>
        <Table className={classes.table}>
          <TableHead>
            <TableRow className={classes.tableRow}>
              <TableCell />
              <TableCell>Name</TableCell>
              <Can
                permissions={['bracket-libraries.update', 'bracket-libraries.manage']}
                organizationId={organizationId}
                yes={() => (
                  <>
                    <TableCell className={classes.shelfHeaderCell}>
                      <Box className={classes.shelfHeader}>
                        <span>Shelf</span>
                        <span>Row</span>
                        <span>Column</span>
                        <span />
                      </Box>
                    </TableCell>
                  </>
                )}
              />
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {data.map(bracket => {
              const { id, label, format } = bracket;
              const rowClasses = [classes.tableRow];
              if (bracket.hidden_at) {
                rowClasses.push(classes.hiddenBracket);
              }
              return (
                <TableRow key={id} className={rowClasses.join(' ')}>
                  <TableCell>
                    <Tooltip title={format === 1 ? 'Original' : 'VTK'} placement="left">
                      <Avatar className={format === 2 ? classes.vtkIconColor : ''}>
                        <FolderIcon />
                      </Avatar>
                    </Tooltip>
                  </TableCell>
                  <TableCell>{label}</TableCell>
                  <Can
                    permissions={['bracket-libraries.update', 'bracket-libraries.manage']}
                    organizationId={organizationId}
                    yes={() => (
                      <>
                        <TableCell className={classes.shelfInputCell}>
                          <ShelfRowColumnInput organizationId={organizationId} bracket={bracket} />
                        </TableCell>
                      </>
                    )}
                  />
                  <TableCell padding="none">
                    <Box
                      display="flex"
                      gap={0}
                      flexWrap="nowrap"
                      justifyContent="flex-end"
                      alignItems="center"
                    >
                      <Can
                        permissions={['bracket-libraries.manage', 'bracket-libraries.view']}
                        organizationId={organizationId}
                        yes={() => (
                          <IconButton
                            edge="end"
                            aria-label="Changelog"
                            onClick={() => showChangelog(bracket)}
                            title="Changelog"
                            disabled={removing}
                          >
                            <ChangelogIcon />
                          </IconButton>
                        )}
                      />
                      <Can
                        permissions={[
                          'bracket-libraries.manage',
                          'organization.bracket-libraries.update',
                          'bracket-libraries.update',
                          'organization.bracket-libraries.view-inventory'
                        ]}
                        organizationId={organizationId}
                        yes={() => (
                          <IconButton
                            edge="end"
                            aria-label="Inventory"
                            onClick={() => onOpenInventory(bracket)}
                            title="Inventory"
                            disabled={removing}
                          >
                            <InventoryIcon className={inventoryIconClassName(bracket)} />
                          </IconButton>
                        )}
                      />
                      <Can
                        permissions={['bracket-libraries.manage']}
                        organizationId={organizationId}
                        yes={() => (
                          <IconButton
                            aria-label="more"
                            aria-controls="long-menu"
                            aria-haspopup="true"
                            onClick={moreOptionHandler(bracket)}
                          >
                            <MoreVertIcon />
                          </IconButton>
                        )}
                      />
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        {currentBracket && (
          <Menu
            id="long-menu"
            anchorEl={anchorEl}
            keepMounted
            open={open}
            onClose={handleClose}
            PaperProps={{
              elevation: 1,
              style: {
                maxHeight: ITEM_HEIGHT * 4.5,
                width: '20ch'
              }
            }}
          >
            <Can
              permissions={[
                'bracket-libraries.manage',
                'organization.bracket-libraries.update',
                'bracket-libraries.update'
              ]}
              organizationId={organizationId}
              yes={() => (
                <MenuItem
                  onClick={createOnMenuClickCallback(() => onEdit(currentBracket))}
                  disabled={removing}
                >
                  Edit
                </MenuItem>
              )}
            />

            <Can
              permissions={['bracket-libraries.update', 'bracket-libraries.manage']}
              organizationId={organizationId}
              yes={() => (
                <MenuItem
                  onClick={createOnMenuClickCallback(createToggleHideHandler(currentBracket))}
                >
                  {currentBracket.hidden_at === null ? 'Hide' : 'Unhide'}
                </MenuItem>
              )}
            />
            <Can
              permissions={['bracket-libraries.manage', 'bracket-libraries.view']}
              organizationId={organizationId}
              yes={() => (
                <MenuItem
                  onClick={createOnMenuClickCallback(() => onDownload(currentBracket.id))}
                  disabled={removing}
                >
                  Download
                </MenuItem>
              )}
            />
            {!currentBracket.replaced_by_id && (
              <Can
                permissions={['bracket-libraries.manage']}
                organizationId={organizationId}
                yes={() => (
                  <MenuItem onClick={createOnMenuClickCallback(() => onReplace(currentBracket))}>
                    Replace
                  </MenuItem>
                )}
              />
            )}
            <Can
              permissions={['bracket-libraries.manage', 'bracket-libraries.delete']}
              organizationId={organizationId}
              yes={() => (
                <ConfirmationDialogOnClickWrapper
                  confirmationTitle={currentBracket?.file_name}
                  confirmationBody={deletionMessage}
                >
                  <MenuItem
                    disabled={removing}
                    onClick={createOnMenuClickCallback(() => onDelete(currentBracket.id))}
                  >
                    Remove
                  </MenuItem>
                </ConfirmationDialogOnClickWrapper>
              )}
            />
          </Menu>
        )}
      </TableContainer>
    </>
  );
};

FileList.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  data: PropTypes.array.isRequired,
  onDelete: PropTypes.func.isRequired,
  onDownload: PropTypes.func.isRequired,
  onOpenInventory: PropTypes.func.isRequired,
  removingItems: PropTypes.arrayOf(PropTypes.number).isRequired,
  onEdit: PropTypes.func.isRequired,
  onReplace: PropTypes.func.isRequired,
  organizationId: PropTypes.number.isRequired,
  showChangelog: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired
};

export default FileList;
